import Button from '@/common/components/Button';
import Checkbox from '@/common/components/Checkbox';
import { CurrencyTextField } from '@/common/components/CurrencyTextField';
import SelectController from '@/common/components/SelectController';
import { UnitTextField } from '@/common/components/UnitTextField';
import { options as unitForQuantity } from "@/common/constants/unitForQuantityOptions.constant";
import { ProductsConsumptionInCurrentPeriodLimitsData } from '@/common/types/productsConsumptionInCurrentPeriodLimits.type';
import { FileBoxIcon, XIcon } from 'lucide-react';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import { options as applicableProductsPeriodLimit } from "../../constants/applicableProductsPeriodLimit.constant";
import { CardProductLimitValidationSchema, EquipmentValidationSchema } from '../../schemas/create.schema';
import { formatCurrency, formatDecimal, formatUnit } from '@/common/utils/formatters';
import Skeleton from '@/common/components/Skeleton';

interface ApplicableProductProps {
  applicableProduct: CardProductLimitValidationSchema;
  applicableProductIndex: number;
  removeApplicableProduct: (index: number) => void;
  limitByTransaction?: boolean;
  limitByPeriod?: boolean;
  control: Control<EquipmentValidationSchema, any>;
  register: UseFormRegister<EquipmentValidationSchema>;
  errors: FieldErrors<EquipmentValidationSchema>;
  disabled?: boolean;
  hideProductConsumption?: boolean;
  productConsumptionInCurrentPeriodLimits?: ProductsConsumptionInCurrentPeriodLimitsData[string];
  productsConsumptionInCurrentPeriodLimitsIsPending: boolean;
}

export const ApplicableProduct = ({
  applicableProduct,
  applicableProductIndex,
  removeApplicableProduct,
  limitByTransaction,
  limitByPeriod,
  control,
  register,
  errors,
  disabled,
  hideProductConsumption,
  productConsumptionInCurrentPeriodLimits,
  productsConsumptionInCurrentPeriodLimitsIsPending,
}: ApplicableProductProps) => {
  const quantitySymbol = unitForQuantity.find((option) => option.value === applicableProduct.unit_for_quantity)?.symbol;

  return (
    <div className="flex w-full flex-col gap-y-6 bg-[#FFFFFF0D] rounded-sm p-4">
      <div className="flex items-center">
        <div className="flex items-center gap-x-2 bg-white-200 p-2">
          <FileBoxIcon strokeWidth={1} />
          <p>{applicableProduct.display_name}</p>
        </div>
        <Button
          onClick={() => removeApplicableProduct(applicableProductIndex)}
          className="bg-black hover:bg-black-900 rounded-none p-2"
        >
          <XIcon strokeWidth={1} />
        </Button>
      </div>
      <div className="flex items-center gap-x-6">
        <Checkbox
          label="Limite por transação"
          labelClassName="text-[10px] italic font-light"
          {...register(`card_product_limits_attributes.${applicableProductIndex}.limitByTransaction`)}
          disabled={disabled}
        />
        <Checkbox
          label="Limite por período"
          labelClassName="text-[10px] italic font-light"
          {...register(`card_product_limits_attributes.${applicableProductIndex}.limitByPeriod`)}
          disabled={disabled}
        />
      </div>
      {limitByTransaction && (
        <div className="flex flex-col gap-y-4">
          <p>Limite por Transação</p>
          <div className="flex w-full gap-x-2">
            <Controller
              name={`card_product_limits_attributes.${applicableProductIndex}.transactionLimit.quantity`}
              control={control}
              disabled={disabled}
              render={({ field }) => (
                <UnitTextField
                  unit={quantitySymbol || ''}
                  label="Quantidade"
                  className="w-full"
                  defaultValue={applicableProduct.transactionLimit?.quantity}
                  errorMessage={errors.card_product_limits_attributes?.[applicableProductIndex]?.transactionLimit?.quantity?.message || undefined}
                  {...field}
                />
              )}
            />
            <p className="mt-9">Ou</p>
            <Controller
              name={`card_product_limits_attributes.${applicableProductIndex}.transactionLimit.totalValue`}
              control={control}
              disabled={disabled}
              render={({ field }) => (
                <CurrencyTextField
                  label="Valor total"
                  className="w-full"
                  errorMessage={errors.card_product_limits_attributes?.[applicableProductIndex]?.transactionLimit?.totalValue?.message || undefined}
                  {...field}
                />
              )}
            />
          </div>
          {errors.card_product_limits_attributes?.[applicableProductIndex]?.transactionLimit?.root?.message && (
            <p className="text-danger-soft text-sm mt-[-8px]">{errors.card_product_limits_attributes?.[applicableProductIndex]?.transactionLimit?.root?.message}</p>
          )}

          <p className="font-light text-xs italic mt-[-8px]">
            Quando não determinado um limite, o motorista irá visualizar o valor de <span className="font-bold not-italic">10.000</span>, para evitar fraude.
          </p>
        </div>
      )}
      {limitByPeriod && (
        <div className="flex flex-col gap-y-4">
          <p>Limite por Período</p>
          <SelectController
            control={control}
            name={`card_product_limits_attributes.${applicableProductIndex}.periodLimit.period`}
            label="Período"
            options={applicableProductsPeriodLimit}
            errorMessage={errors.card_product_limits_attributes?.[applicableProductIndex]?.periodLimit?.period?.message || undefined}
            isDisabled={disabled}
          />
          <div className="flex w-full gap-x-2">
            <div className="flex w-full flex-col gap-y-1">
              <Controller
                name={`card_product_limits_attributes.${applicableProductIndex}.periodLimit.quantity`}
                control={control}
                disabled={disabled}
                render={({ field }) => (
                  <UnitTextField
                    unit={quantitySymbol || ''}
                    label="Quantidade"
                    className="w-full"
                    defaultValue={applicableProduct.periodLimit?.quantity}
                    errorMessage={errors.card_product_limits_attributes?.[applicableProductIndex]?.periodLimit?.quantity?.message || undefined}
                    {...field}
                  />
                )}
              />
              {!hideProductConsumption &&
                (!productsConsumptionInCurrentPeriodLimitsIsPending ? (
                  <p className="italic text-xs text-white-400">
                    Quantidade utilizada:{' '}
                    {formatUnit(
                      formatDecimal(productConsumptionInCurrentPeriodLimits?.quantity_total || 0),
                      applicableProduct.unit_for_quantity || '',
                    )}
                  </p>
                ) : (
                  <Skeleton className="w-32 h-4" />
                ))}
            </div>
            <p className="mt-9">Ou</p>
            <div className="flex w-full flex-col gap-y-1">
              <Controller
                name={`card_product_limits_attributes.${applicableProductIndex}.periodLimit.totalValue`}
                control={control}
                disabled={disabled}
                render={({ field }) => (
                  <CurrencyTextField
                    label="Valor total"
                    className="w-full"
                    errorMessage={errors.card_product_limits_attributes?.[applicableProductIndex]?.periodLimit?.totalValue?.message || undefined}
                    {...field}
                  />
                )}
              />
              {!hideProductConsumption &&
                (!productsConsumptionInCurrentPeriodLimitsIsPending ? (
                  <p className="italic text-xs text-white-400">
                    Valor utilizado:{' '}
                    {formatCurrency(Number(productConsumptionInCurrentPeriodLimits?.consumed_total || 0))}
                  </p>
                ) : (
                  <Skeleton className="w-32 h-4" />
                ))}
            </div>
          </div>
          {errors.card_product_limits_attributes?.[applicableProductIndex]?.periodLimit?.root?.message && (
            <p className="text-danger-soft text-sm mt-[-8px]">{errors.card_product_limits_attributes?.[applicableProductIndex]?.periodLimit?.root?.message}</p>
          )}

          <p className="font-light text-xs italic mt-[-8px]">
            Quando não determinado um limite, o motorista irá visualizar o valor de <span className="font-bold not-italic">10.000</span>, para evitar fraude.
          </p>
        </div>
      )}
    </div>
  );
};
